import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import API from "../utils/api";
import { resolveAssetUrl } from "../utils/config";
import "../styles/boardDetail.css";

function BoardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 로그인 정보 (AuthContext 없이 localStorage 기반)
  const userInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"));
    } catch {
      return null;
    }
  })();
  const isLoggedIn = !!userInfo;
  const isAuthor = userInfo && userInfo.name === post?.author;

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);

    API.get(`/board/${id}`)
      .then((res) => { if (!ignore) setPost(res.data.content); })
      .catch(() => { if (!ignore) setError("게시글을 불러오지 못했습니다."); })
      .finally(() => { if (!ignore) setLoading(false); });

    return () => { ignore = true; };
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("정말로 삭제하시겠습니까?")) return;
    try {
      await API.delete(`/board/${id}`);
      alert("게시글이 삭제되었습니다.");
      navigate("/board");
    } catch {
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  const handleEdit = () => navigate(`/board/edit/${id}`, { state: { post } });

  if (loading) {
    return (
      <div className="container section state-block">
        <div className="spinner" />
        로딩 중...
      </div>
    );
  }
  if (error) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">{error}</p>
        <button className="btn btn--outline btn--sm" onClick={() => navigate("/board")}>목록으로</button>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">게시글을 찾을 수 없습니다.</p>
      </div>
    );
  }

  const rawContent = post.content || "";
  const prepared = /<[a-z][\s\S]*>/i.test(rawContent) ? rawContent : rawContent.replace(/\n/g, "<br>");
  const contentHtml = DOMPurify.sanitize(prepared);

  return (
    <article className="board-detail container container--narrow section">
      <div className="detail-topbar">
        <button className="btn btn--ghost btn--sm" onClick={() => navigate("/board")}>← 목록으로</button>
        {isLoggedIn && isAuthor && (
          <div className="detail-actions">
            <button className="btn btn--outline btn--sm" onClick={handleEdit}>수정</button>
            <button className="btn btn--danger btn--sm" onClick={handleDelete}>삭제</button>
          </div>
        )}
      </div>

      <header className="detail-header">
        <h1 className="detail-title">{post.title}</h1>
        <div className="detail-meta">
          <span>{post.author}</span>
          <span>{new Date(post.createdAt).toLocaleDateString()}</span>
          <span>조회 {post.viewCount}</span>
        </div>
      </header>

      <div className="detail-content detail-content--rich" dangerouslySetInnerHTML={{ __html: contentHtml }} />

      {post.images && post.images.length > 0 && (
        <section className="board-attachments">
          <h2 className="board-attachments__title">첨부 이미지</h2>
          <div className="board-images-grid">
            {post.images.map((image, index) => (
              <img
                key={image.id ?? index}
                src={resolveAssetUrl(image.url)}
                alt={`첨부 이미지 ${index + 1}`}
                loading="lazy"
                className="board-image"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ))}
          </div>
        </section>
      )}

      {post.attachments && post.attachments.length > 0 && (
        <section className="board-attachments">
          <h2 className="board-attachments__title">첨부파일</h2>
          <ul className="attachment-list">
            {post.attachments.map((att, index) => {
              const ext = att.originalFileName?.split(".").pop()?.toUpperCase() || "FILE";
              return (
                <li key={att.id ?? index} className="attachment-item">
                  <span className="attachment-ext">{ext}</span>
                  <a className="attachment-name" href={resolveAssetUrl(att.filePath)} download={att.originalFileName}>
                    {att.originalFileName}
                  </a>
                  <a className="btn btn--ghost btn--sm" href={resolveAssetUrl(att.filePath)} download={att.originalFileName}>
                    다운로드
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </article>
  );
}

export default BoardDetail;
